import React, { Fragment, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Box from '@nubank/nuds-web/components/Box/Box';

import { trackRTRAppStoreClick } from 'tracking/application/rtr';
import sanitizer from 'utils/sanitizer';
import AppStoreBadge from 'components/AppStoreBadge/AppStoreBadge';
import Collapse from 'components/Collapse/Collapse';
import { ProspectRegistrationDrawerContext } from 'patterns/ProspectRegistrationDrawer/ProspectRegistrationDrawerContext';

import FeedbackVirtualCardIcon from '../FeedbackVirtualCardIcon/FeedbackVirtualCardIcon';

import FeedbackAnticipateInstallmentIcon from './components/FeedbackAnticipateInstallmentIcon/FeedbackAnticipateInstallmentIcon';
import FeedbackGiveMoneyIcon from './components/FeedbackGiveMoneyIcon/FeedbackGiveMoneyIcon';
import FeedbackCustomSettingsIcon from './components/FeedbackCustomSettingsIcon/FeedbackCustomSettingsIcon';
import FeedbackClockIcon from './components/FeedbackClockIcon/FeedbackClockIcon';
import FeedbackCardIcon from './components/FeedbackCardIcon/FeedbackCardIcon';
import FeedbackMoneyCoinsIcon from './components/FeedbackMoneyCoinsIcon/FeedbackMoneyCoinsIcon';
import FeedbackYieldIcon from './components/FeedbackYieldIcon/FeedbackYieldIcon';
import FeedbackMoneyBillStackIcon from './components/FeedbackMoneyBillStackIcon/FeedbackMoneyBillStackIcon';
import StyledStoreIconButton from './styles/StyledStoreIconButton';
import { Container } from './styles/Container';
import { Section, SubSection } from './styles/Section';
import {
  ListItem,
} from './styles/List';

const FeedbackApprovedIcon = dynamic(() => import('./components/FeedbackApprovedIcon/FeedbackApprovedIcon'), { ssr: false });

const ICONS = {
  anticipate_installment: <FeedbackAnticipateInstallmentIcon />,
  money_coins: <FeedbackMoneyCoinsIcon />,
  give_money: <FeedbackGiveMoneyIcon />,
  custom_settings: <FeedbackCustomSettingsIcon />,
  virtual_card: <FeedbackVirtualCardIcon />,
  yield: <FeedbackYieldIcon />,
  money_bill_stack: <FeedbackMoneyBillStackIcon />,
  clock: <FeedbackClockIcon />,
  card: <FeedbackCardIcon />,
};

function FeedbackApproved({
  className,
  template,
  prospect,
}) {
  const widget = useContext(ProspectRegistrationDrawerContext);

  const handleAppStoreClick = store => () => {
    trackRTRAppStoreClick({
      ...prospect,
      store,
    });
  };

  useEffect(() => {
    widget.toggleTheme('dark');
  }, []);

  return (
    <Container className={className}>
      <Box padding="1rem 2rem 0">
        <FeedbackApprovedIcon />
      </Box>
      <Box
        padding="0 2rem 2rem"
        overflowY="hidden"
      >
        <Box marginBottom="6x">
          <Typography color="black" variant="subtitle" colorVariant="light">
            {template.title}
          </Typography>
        </Box>
        <Box marginBottom="6x">
          <Typography variant="text2" color="black" colorVariant="light">
            {template.subtitle}
          </Typography>
        </Box>
        <Box display="flex">
          <StyledStoreIconButton
            href="https://nubank.app.link/aUvpOf6NP6"
            intlAriaKey="FOOTER.APP.APPLE"
            onClick={handleAppStoreClick('ios')}
          >
            <AppStoreBadge platform="ios" variant="light" />
          </StyledStoreIconButton>
          <StyledStoreIconButton
            href="https://nubank.app.link/p9NYaS0NP6"
            intlAriaKey="FOOTER.APP.ANDROID"
            onClick={handleAppStoreClick('android')}
          >
            <AppStoreBadge platform="android" variant="light" />
          </StyledStoreIconButton>
        </Box>

        {template.sections && template.sections.map((section, indexSection) => (
          <Section key={indexSection.toString()}>
            {section.subsections && section.subsections.map((subsection, indexSubSection) => (
              <Fragment key={indexSubSection.toString()}>
                <SubSection>
                  <Collapse
                    initiallyOpen={subsection.is_open}
                    buttonId={`button-${subsection.title}`}
                    contentId={`button-${subsection.title}`}
                  >
                    <Collapse.Header>
                      <Typography color="black" colorVariant="light" variant="paragraph2">
                        {subsection.title}
                      </Typography>
                    </Collapse.Header>

                    <Collapse.Body>
                      {subsection.description && (
                        <Typography variant="paragraph1">
                          {sanitizer.forJSON(subsection.description)}
                        </Typography>
                      )}

                      {subsection.items && (
                        <Box tag="ul">
                          {subsection.items.map((item, indexItem) => (
                            <ListItem key={indexItem.toString()}>
                              <Box
                                tag="span"
                                display="inline-flex"
                                minWidth="6x"
                                flexShrink="0"
                                justifyContent="center"
                                marginRight="4x"
                              >
                                {ICONS[item.icon]}
                              </Box>
                              <Typography variant="text2">{sanitizer.forJSON(item.description)}</Typography>
                            </ListItem>
                          ))}
                        </Box>
                      )}
                    </Collapse.Body>
                  </Collapse>
                </SubSection>
              </Fragment>
            ))}
          </Section>
        ))}
      </Box>
    </Container>
  );
}

FeedbackApproved.defaultProps = {
  className: null,
};

FeedbackApproved.propTypes = {
  className: PropTypes.string,
  prospect: PropTypes.shape({
    marketingId: PropTypes.string.isRequired,
    prospectType: PropTypes.string.isRequired,
  }).isRequired,
  template: PropTypes.oneOfType([
    PropTypes.object,
  ]).isRequired,
};

export default FeedbackApproved;
